import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getModulesCatalog, getTenantModules, updateTenantModules } from '../../services/apiSuperAdmin';

/**
 * Panel para habilitar/deshabilitar módulos y sus funciones para un tenant.
 *
 * El catálogo (qué existe) viene de config/modules.php en el backend, así que
 * agregar un módulo allá lo hace aparecer acá sin tocar este archivo.
 */
export default function TenantModulesModal({ isOpen, onClose, tenant }) {
    const qc = useQueryClient();
    const tenantId = tenant?.id;

    const [draft, setDraft] = useState({});
    const [expanded, setExpanded] = useState({});
    const [search, setSearch] = useState('');
    const [saveError, setSaveError] = useState('');

    const { data: catalog, isLoading: loadingCatalog } = useQuery({
        queryKey: ['super-admin-modules-catalog'],
        queryFn: getModulesCatalog,
        enabled: isOpen,
        staleTime: Infinity, // el catálogo sólo cambia al desplegar
    });

    const { data: current, isLoading: loadingCurrent } = useQuery({
        queryKey: ['super-admin-tenant-modules', tenantId],
        queryFn: () => getTenantModules(tenantId),
        enabled: isOpen && !!tenantId,
    });

    // Se copia el estado del servidor a un borrador local para poder tocar
    // varios interruptores y guardar una sola vez.
    useEffect(() => {
        if (current) setDraft(structuredClone(current));
        setSaveError('');
        setSearch('');
    }, [current, tenantId]);

    const mut = useMutation({
        mutationFn: () => updateTenantModules(tenantId, draft),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['super-admin-tenant-modules', tenantId] });
            qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
            onClose();
        },
        onError: (err) => {
            setSaveError(err.response?.data?.message || 'No se pudieron guardar los cambios.');
        },
    });

    const visibleModules = useMemo(() => {
        if (!catalog) return [];
        const q = search.trim().toLowerCase();
        if (!q) return catalog;
        return catalog.filter(m =>
            m.label.toLowerCase().includes(q) ||
            m.features.some(f => f.label.toLowerCase().includes(q))
        );
    }, [catalog, search]);

    const stats = useMemo(() => {
        if (!catalog) return { modulesOff: 0, featuresOff: 0 };
        let modulesOff = 0;
        let featuresOff = 0;
        for (const m of catalog) {
            const entry = draft[m.key];
            if (!entry) continue;
            if (entry.enabled === false) { modulesOff++; continue; }
            for (const f of m.features) {
                if (entry.features?.[f.key] === false) featuresOff++;
            }
        }
        return { modulesOff, featuresOff };
    }, [catalog, draft]);

    if (!isOpen) return null;

    const isLoading = loadingCatalog || loadingCurrent;

    const setModuleEnabled = (moduleKey, enabled) => {
        setDraft(prev => ({
            ...prev,
            [moduleKey]: { ...(prev[moduleKey] ?? { features: {} }), enabled },
        }));
        if (enabled) setExpanded(prev => ({ ...prev, [moduleKey]: true }));
    };

    const setFeatureEnabled = (moduleKey, featureKey, enabled) => {
        setDraft(prev => ({
            ...prev,
            [moduleKey]: {
                ...(prev[moduleKey] ?? { enabled: true }),
                features: { ...(prev[moduleKey]?.features ?? {}), [featureKey]: enabled },
            },
        }));
    };

    const setAll = (enabled) => {
        if (!catalog) return;
        const next = {};
        for (const m of catalog) {
            const features = {};
            for (const f of m.features) features[f.key] = enabled;
            next[m.key] = { enabled, features };
        }
        setDraft(next);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-3xl bg-white dark:bg-background-dark rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="px-7 py-5 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="size-10 shrink-0 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center text-violet-600 dark:text-violet-400">
                            <span className="material-symbols-outlined">widgets</span>
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 truncate">
                                Módulos y funciones
                            </h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {tenant?.name}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors shrink-0">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Toolbar */}
                {!isLoading && (
                    <div className="px-7 py-3 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-3 bg-slate-50/60 dark:bg-slate-900/30">
                        <div className="relative flex-1 min-w-[180px]">
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Buscar módulo o función..."
                                className="w-full h-9 pl-9 pr-3 text-sm rounded-lg border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-violet-500 focus:ring-violet-500/20"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setAll(true)}
                                className="px-3 h-9 text-xs font-bold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
                            >
                                Habilitar todo
                            </button>
                            <button
                                onClick={() => setAll(false)}
                                className="px-3 h-9 text-xs font-bold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-red-400 hover:text-red-600 transition-colors"
                            >
                                Deshabilitar todo
                            </button>
                        </div>
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-7 py-5">
                    {isLoading ? (
                        <div className="py-16 flex flex-col items-center gap-3">
                            <div className="w-7 h-7 rounded-full border-4 border-violet-500/30 border-t-violet-600 animate-spin" />
                            <p className="text-xs text-slate-400">Cargando módulos...</p>
                        </div>
                    ) : visibleModules.length === 0 ? (
                        <div className="py-16 text-center text-sm text-slate-400">
                            Ningún módulo coincide con “{search}”.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {visibleModules.map(module => {
                                const entry = draft[module.key] ?? {};
                                const moduleOn = entry.enabled !== false;
                                const hasFeatures = module.features.length > 0;
                                const isOpenRow = expanded[module.key] ?? false;
                                const offCount = hasFeatures
                                    ? module.features.filter(f => entry.features?.[f.key] === false).length
                                    : 0;

                                return (
                                    <div
                                        key={module.key}
                                        className={`rounded-xl border transition-colors ${moduleOn
                                            ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40'
                                            : 'border-slate-200/70 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/20'
                                            }`}
                                    >
                                        {/* Module row */}
                                        <div className="flex items-center gap-3 p-4">
                                            <div className={`size-9 shrink-0 rounded-lg flex items-center justify-center ${moduleOn
                                                ? 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400'
                                                : 'bg-slate-100 text-slate-400 dark:bg-slate-800'
                                                }`}>
                                                <span className="material-symbols-outlined text-[20px]">{module.icon}</span>
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className={`text-sm font-bold ${moduleOn ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400'}`}>
                                                        {module.label}
                                                    </span>
                                                    {!moduleOn && (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                                                            Oculto
                                                        </span>
                                                    )}
                                                    {moduleOn && offCount > 0 && (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                                                            {offCount} función{offCount > 1 ? 'es' : ''} off
                                                        </span>
                                                    )}
                                                </div>
                                                {module.description && (
                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                        {module.description}
                                                    </p>
                                                )}
                                            </div>

                                            {hasFeatures && (
                                                <button
                                                    onClick={() => setExpanded(prev => ({ ...prev, [module.key]: !isOpenRow }))}
                                                    disabled={!moduleOn}
                                                    className="shrink-0 flex items-center gap-1 text-[11px] font-bold text-slate-400 hover:text-violet-600 disabled:opacity-40 disabled:hover:text-slate-400 transition-colors"
                                                >
                                                    {module.features.length} func.
                                                    <span className={`material-symbols-outlined text-[16px] transition-transform ${isOpenRow ? 'rotate-180' : ''}`}>
                                                        expand_more
                                                    </span>
                                                </button>
                                            )}

                                            <Toggle
                                                checked={moduleOn}
                                                onChange={(v) => setModuleEnabled(module.key, v)}
                                            />
                                        </div>

                                        {/* Features */}
                                        {hasFeatures && isOpenRow && moduleOn && (
                                            <div className="border-t border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                                                {module.features.map(feature => {
                                                    const featureOn = entry.features?.[feature.key] !== false;
                                                    return (
                                                        <div key={feature.key} className="flex items-center gap-3 pl-16 pr-4 py-2.5">
                                                            <div className="flex-1 min-w-0">
                                                                <span className={`text-[13px] font-semibold ${featureOn ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'}`}>
                                                                    {feature.label}
                                                                </span>
                                                                {feature.description && (
                                                                    <p className="text-[11px] text-slate-400 mt-0.5">{feature.description}</p>
                                                                )}
                                                            </div>
                                                            <Toggle
                                                                small
                                                                checked={featureOn}
                                                                onChange={(v) => setFeatureEnabled(module.key, feature.key, v)}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-7 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-4">
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {stats.modulesOff === 0 && stats.featuresOff === 0
                            ? 'Todo habilitado.'
                            : `${stats.modulesOff} módulo(s) y ${stats.featuresOff} función(es) deshabilitadas.`}
                        {saveError && <span className="block text-red-500 font-semibold mt-1">{saveError}</span>}
                    </p>
                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            onClick={onClose}
                            className="px-5 py-2 text-sm text-slate-500 font-bold hover:text-slate-700 dark:hover:text-slate-300 rounded-lg transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={() => { setSaveError(''); mut.mutate(); }}
                            disabled={mut.isPending || isLoading}
                            className="px-6 py-2 text-sm bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-lg shadow-lg shadow-violet-600/20 transition-all flex items-center gap-2 disabled:opacity-60"
                        >
                            {mut.isPending && <span className="material-symbols-outlined text-[18px] animate-spin">refresh</span>}
                            Guardar cambios
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Toggle({ checked, onChange, small = false }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={`relative inline-flex shrink-0 items-center rounded-full transition-colors ${small ? 'h-5 w-9' : 'h-6 w-11'
                } ${checked ? 'bg-violet-600' : 'bg-slate-300 dark:bg-slate-700'}`}
        >
            <span
                className={`inline-block transform rounded-full bg-white shadow transition-transform ${small ? 'h-3.5 w-3.5' : 'h-4 w-4'
                    } ${checked
                        ? (small ? 'translate-x-[1.15rem]' : 'translate-x-6')
                        : 'translate-x-1'
                    }`}
            />
        </button>
    );
}
